import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { firestoreService } from '../services/firestoreService';
import MapView, { MapMarker, MapPolyline } from '../components/Map/MapView';
import { callGetOrderEta, callGetRoute, RouteResult } from '../services/cloudFunctions';
import { volver } from '../utils/volver';
import { pesos } from '../utils/dinero';

/**
 * Pantalla de seguimiento en vivo del pedido.
 *
 * - Suscripción Firestore al doc /food_orders/{id} → status + driverId
 * - Suscripción a /driverLocations/{driverId} → lat/lng en vivo
 * - ETA refrescado cada 60s vía callable getOrderEta
 *
 * HONESTIDAD DEL MAPA (lo que arregla esta versión):
 * `firestoreService.listenToDriverLocation` devuelve `null` TANTO cuando la
 * regla deniega la lectura COMO cuando el repartidor todavía no ha escrito su
 * posición. Desde aquí no se puede saber la causa — y no se inventa. Lo que sí
 * se puede saber, y decir, es que NO hay posición. Antes esta pantalla
 * ignoraba el `null` en silencio: el cliente se quedaba mirando un mapa sin
 * repartidor bajo un título que promete "Seguir mi pedido", para siempre.
 *
 * Ahora distingue y dice cuatro cosas:
 *   · todavía no hay repartidor asignado
 *   · todavía no hay posición (con PLAZO: `GRACIA_POSICION_MS`)
 *   · no se pudo seguir (plazo vencido, o posición que dejó de actualizarse)
 *   · ya no hay nada que seguir (entregado / cancelado)
 * Y en todos los casos deja a la vista el estado del pedido y cómo contactar.
 */

/** Plazo para la PRIMERA posición del repartidor antes de admitir el fallo. */
const GRACIA_POSICION_MS = 25_000;
/** Sin actualizaciones durante este tiempo, el punto del mapa ya no es "en vivo". */
const POSICION_RANCIA_MS = 120_000;
/** Plazo para que llegue el PIN privado antes de decir que no se pudo leer. */
const GRACIA_PIN_MS = 12_000;

type EstadoSeguimiento =
  | 'TERMINADO'       // entregado o cancelado: ya no hay nada que seguir
  | 'SIN_REPARTIDOR'  // el pedido aún no tiene chofer asignado
  | 'BUSCANDO'        // hay chofer, esperando su primera posición (con plazo)
  | 'EN_VIVO'         // posición fresca
  | 'RANCIA'          // hubo posición, dejó de actualizarse
  | 'SIN_SENAL';      // plazo vencido sin una sola posición

export default function OrderTrackingScreen() {
  const router = useRouter();
  const { orderId } = useLocalSearchParams<{ orderId: string }>();
  const [order, setOrder] = useState<any | null>(null);
  // La posición viaja SIEMPRE con el chofer al que pertenece y con la hora en
  // que entró: así una posición del chofer anterior no puede colarse como si
  // fuera la del actual, y "hace cuánto" no es una suposición.
  const [posicion, setPosicion] = useState<
    { lat: number; lng: number; driverId: string; at: number } | null
  >(null);
  const [eta, setEta] = useState<{ etaMinutes: number; leg?: string; distanceKm?: number } | null>(null);
  const [route, setRoute] = useState<RouteResult | null>(null);
  const [loading, setLoading] = useState(true);

  // PIN de entrega — leído de la subcolección PRIVADA (solo el cliente puede).
  const [orderPin, setOrderPin] = useState<string | null>(null);
  // Momento en que se pidió el PIN — para no dejar '----' eterno si no llega.
  const [pinDesde] = useState<number>(() => Date.now());

  // Relojes del seguimiento. Sin ellos, "esperando" y "no se pudo" son lo
  // mismo en pantalla. `ahora` es el latido que hace vencer los plazos.
  // `sondeo` guarda DESDE CUÁNDO se espera la posición de ESTE chofer: se
  // reinicia cuando cambia el repartidor y cuando el cliente reintenta.
  const [sondeo, setSondeo] = useState<{ driverId: string | null; desde: number }>(
    () => ({ driverId: null, desde: Date.now() })
  );
  const [reintentoPos, setReintentoPos] = useState(0);
  const [ahora, setAhora] = useState<number>(() => Date.now());

  const terminal = order?.status === 'DELIVERED' || order?.status === 'CANCELLED';

  // Posición VÁLIDA para pintar: la del chofer actual y solo mientras el
  // pedido sigue vivo. Derivada, no copiada: no hay estado que limpiar.
  const driverLoc =
    !terminal && order?.driverId && posicion?.driverId === order.driverId ? posicion : null;
  const ultimaPosicionAt = driverLoc ? driverLoc.at : null;

  // 1) Listener del pedido
  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }
    const unsub = firestoreService.listenToFoodOrderById(orderId, (o) => {
      setOrder(o);
      setLoading(false);
      // El plazo de la posición empieza cuando aparece (o cambia) el chofer.
      const d = (o?.driverId as string | undefined) ?? null;
      setSondeo((s) => (s.driverId === d ? s : { driverId: d, desde: Date.now() }));
    });
    // PIN privado — el cliente lo dicta al repartidor al recibir.
    const unsubPin = firestoreService.listenToOrderPin(orderId, (p) => setOrderPin(p));
    return () => { unsub(); try { (unsubPin as any)?.(); } catch {} };
  }, [orderId]);

  // 2) Listener de ubicación del driver (cuando ya hay driverId)
  //    El callback llega con `null` tanto si la regla deniega como si el
  //    repartidor aún no ha escrito posición. No se adivina la causa: se
  //    apunta la hora y la pantalla lo cuenta como "sin posición".
  //
  //    V14 — el permiso de leer al repartidor lo concede un disparador
  //    (permisoUbicacionDelPedido) SEGUNDOS después del ASSIGNED, y un
  //    onSnapshot denegado por reglas no se reintenta solo. Mientras no haya
  //    llegado ninguna posición válida, un `null` reabre la suscripción con los
  //    mismos plazos crecientes que los viajes (PLAZOS_POSICION_CHOFER, map.tsx).
  //    Agotados, queda el aviso SIN_SENAL con su «Reintentar». Las reaperturas
  //    no tocan `sondeo`: el plazo de 25 s sigue corriendo. `order?.status` en
  //    las dependencias reabre al pasar a PICKED_UP, cuando el permiso se
  //    vuelve a sincronizar.
  const estadoDelPedido = order?.status;
  useEffect(() => {
    const driverId = order?.driverId;
    if (!driverId || terminal) return;
    const PLAZOS_REAPERTURA = [4000, 6000, 10000, 15000];
    let vivo = true;
    let intento = 0;
    let huboPosicion = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let unsub: (() => void) | null = null;

    const abrir = () => {
      unsub = firestoreService.listenToDriverLocation(driverId, (loc: any) => {
        if (!vivo) return;
        if (loc && Number.isFinite(loc.lat) && Number.isFinite(loc.lng)) {
          huboPosicion = true;
          setPosicion({ lat: loc.lat, lng: loc.lng, driverId, at: Date.now() });
          setAhora(Date.now());
        } else {
          // Sin posición legible. No se conserva la anterior: un punto viejo
          // presentado como actual es exactamente la mentira que evitamos.
          setPosicion((p) => (p && p.driverId === driverId ? null : p));
          setAhora(Date.now());
          if (
            !huboPosicion &&
            (estadoDelPedido === 'ASSIGNED' || estadoDelPedido === 'PICKED_UP') &&
            intento < PLAZOS_REAPERTURA.length &&
            !timer
          ) {
            const espera = PLAZOS_REAPERTURA[intento++];
            timer = setTimeout(() => {
              timer = null;
              if (!vivo || huboPosicion) return;
              try { unsub?.(); } catch {}
              abrir();
            }, espera);
          }
        }
      }) as any;
    };
    abrir();

    return () => {
      vivo = false;
      if (timer) clearTimeout(timer);
      try { unsub?.(); } catch {}
    };
  }, [order?.driverId, terminal, reintentoPos, estadoDelPedido]);

  // Salida del usuario cuando el seguimiento falla: reabre la suscripción y
  // reinicia el plazo EN EL ACTO (el reloj de pantalla tarda hasta 5s, y un
  // botón que parece no hacer nada es otra forma de mentir).
  const reintentarSeguimiento = React.useCallback(() => {
    setSondeo((s) => ({ driverId: s.driverId, desde: Date.now() }));
    setAhora(Date.now());
    setReintentoPos((k) => k + 1);
  }, []);

  // 2b) Reloj de pantalla — hace vencer los plazos de arriba. Se apaga solo
  //     cuando ya no hay nada que cronometrar (pedido terminado o sin cargar).
  const hayPedido = !!order;
  useEffect(() => {
    if (!hayPedido || terminal) return;
    const id = setInterval(() => setAhora(Date.now()), 5_000);
    return () => clearInterval(id);
  }, [hayPedido, terminal]);

  // 3) Refresh ETA cada 60s — se detiene en cuanto el pedido termina.
  useEffect(() => {
    if (!orderId || terminal) return;
    let stopped = false;
    const fetchEta = async () => {
      try {
        const r = await callGetOrderEta({ orderId });
        if (!stopped) setEta(r.data);
      } catch {
        // Sin ETA no se pinta ETA. Nunca se rellena con un número inventado.
        if (!stopped) setEta(null);
      }
    };
    fetchEta();
    const id = setInterval(fetchEta, 60_000);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [orderId, terminal]);

  // 4) Ruta REAL (Google Directions API) — refrescada cuando cambia el leg
  //    o cuando el driver se mueve significativamente (> 500m de la última call).
  //    Cache server-side 1h por ruta, así si el chofer hace zigzag no
  //    se gastan llamadas adicionales.
  // ── V21 — EL DESTINO EXACTO YA NO VIVE EN EL PEDIDO ─────────────────────
  // La dirección y el GPS del cliente se mudaron a `private/contacto`, porque
  // el documento del pedido lo lee entero cualquier repartidor verificado
  // mientras está en READY. Esta pantalla es del cliente (y del repartidor ya
  // asignado): los dos tienen permiso de leer ese documento privado.
  // `deliveryAproxLat/Lng` (redondeado a ~1 km) queda de respaldo para pedidos
  // creados antes del cambio, para que el mapa nunca se quede sin destino.
  const [destinoPrivado, setDestinoPrivado] = useState<
    { lat: number | null; lng: number | null; direccion: string | null } | null
  >(null);
  useEffect(() => {
    if (!orderId) return;
    return firestoreService.escucharPrivadoDelPedido(String(orderId), 'contacto', (d) =>
      setDestinoPrivado(
        d
          ? {
              lat: typeof d.deliveryLat === 'number' ? d.deliveryLat : null,
              lng: typeof d.deliveryLng === 'number' ? d.deliveryLng : null,
              direccion: String(d.deliveryAddress ?? '').trim() || null,
            }
          : null
      )
    );
  }, [orderId]);

  const numeroONulo = (v: unknown): number | null => (typeof v === 'number' ? v : null);
  const destinoLat =
    destinoPrivado?.lat ?? numeroONulo(order?.deliveryLat) ?? numeroONulo(order?.deliveryAproxLat);
  const destinoLng =
    destinoPrivado?.lng ?? numeroONulo(order?.deliveryLng) ?? numeroONulo(order?.deliveryAproxLng);
  const destinoDireccion =
    destinoPrivado?.direccion ??
    (String(order?.deliveryAddress ?? '').trim() || null) ??
    (String(order?.zonaEntrega ?? '').trim() || null);

  const lastRoutedLoc = React.useRef<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    if (!order || !driverLoc || terminal) return;
    const status = order.status;
    let dest: { lat: number; lng: number } | null = null;
    if (status === 'ASSIGNED' && typeof order.pickupLat === 'number' && typeof order.pickupLng === 'number') {
      dest = { lat: order.pickupLat, lng: order.pickupLng };
    } else if (status === 'PICKED_UP' && destinoLat !== null && destinoLng !== null) {
      dest = { lat: destinoLat, lng: destinoLng };
    }
    if (!dest) return;

    // Si el driver no se movió > 500m desde la última llamada, no refetcheamos
    if (lastRoutedLoc.current) {
      const dLat = driverLoc.lat - lastRoutedLoc.current.lat;
      const dLng = driverLoc.lng - lastRoutedLoc.current.lng;
      const approxMeters = Math.sqrt(dLat * dLat + dLng * dLng) * 111000;
      if (approxMeters < 500) return;
    }
    lastRoutedLoc.current = { ...driverLoc };

    let cancelled = false;
    callGetRoute({ origin: { lat: driverLoc.lat, lng: driverLoc.lng }, destination: dest, mode: 'driving' })
      .then((r) => {
        if (cancelled) return;
        if (r.data?.coordinates?.length) {
          setRoute(r.data);
        }
      })
      .catch((e) => {
        console.warn('[order-tracking] route fetch falló:', e?.code, e?.message);
      });
    return () => { cancelled = true; };
  }, [order?.status, order?.pickupLat, order?.pickupLng, destinoLat, destinoLng, driverLoc?.lat, driverLoc?.lng, terminal, order, driverLoc]);

  // 5) Plazo del escudo de carga: si el pedido no llega en 15s, se dice.
  const [cargaVencida, setCargaVencida] = useState(false);
  useEffect(() => {
    if (!loading) return;
    const id = setTimeout(() => setCargaVencida(true), 15_000);
    return () => clearTimeout(id);
  }, [loading, orderId]);

  if (loading && !cargaVencida) {
    return (
      <SafeAreaView style={[styles.safe, { justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
        <ActivityIndicator size="large" color={Colors.primary} />
        <Text style={styles.cargandoTexto}>Cargando tu pedido…</Text>
      </SafeAreaView>
    );
  }

  if (!order) {
    return (
      <SafeAreaView style={[styles.safe, { justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
        <Ionicons name="alert-circle-outline" size={56} color={Colors.border} />
        <Text style={styles.vacioTitulo}>
          {loading ? 'No pudimos cargar tu pedido' : 'Pedido no encontrado'}
        </Text>
        <Text style={styles.vacioDesc}>
          {loading
            ? 'La conexión está tardando más de lo normal. Seguimos intentándolo: si el pedido llega, esta pantalla se actualiza sola.'
            : 'Puede que ya no exista o que esta cuenta no tenga permiso para verlo.'}
        </Text>
        <TouchableOpacity style={styles.botonSalida} onPress={() => volver(router)}>
          <Ionicons name="arrow-back" size={18} color={Colors.primary} />
          <Text style={styles.botonSalidaTexto}>Volver</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  // Decide centro y markers del mapa según etapa
  const restCoord =
    typeof order.pickupLat === 'number' && typeof order.pickupLng === 'number'
      ? { latitude: order.pickupLat, longitude: order.pickupLng }
      : null;
  const dropCoord =
    destinoLat !== null && destinoLng !== null
      ? { latitude: destinoLat, longitude: destinoLng }
      : null;
  const driverCoord = driverLoc
    ? { latitude: driverLoc.lat, longitude: driverLoc.lng }
    : null;

  // Centro del mapa
  let region = restCoord ?? dropCoord ?? driverCoord;
  if (!region) {
    region = { latitude: 18.4861, longitude: -69.9312 }; // Santo Domingo fallback
  }

  // ── Qué está pasando REALMENTE con el seguimiento ─────────────────────────
  // Se decide con relojes, no con suposiciones. `null` del listener puede ser
  // permiso denegado o GPS que aún no llega: no se nombra la causa, se nombra
  // el hecho (no hay posición) y se ofrece salida.
  const desdeUltimaPosicion = ultimaPosicionAt ? Math.max(0, ahora - ultimaPosicionAt) : null;
  const desdeSondeo = Math.max(0, ahora - sondeo.desde);

  let seguimiento: EstadoSeguimiento;
  if (terminal) {
    seguimiento = 'TERMINADO';
  } else if (!order.driverId) {
    seguimiento = 'SIN_REPARTIDOR';
  } else if (driverLoc && desdeUltimaPosicion !== null && desdeUltimaPosicion <= POSICION_RANCIA_MS) {
    seguimiento = 'EN_VIVO';
  } else if (driverLoc) {
    seguimiento = 'RANCIA';
  } else if (desdeSondeo < GRACIA_POSICION_MS) {
    seguimiento = 'BUSCANDO';
  } else {
    seguimiento = 'SIN_SENAL';
  }

  const minutosSinActualizar =
    desdeUltimaPosicion !== null ? Math.max(1, Math.round(desdeUltimaPosicion / 60_000)) : null;

  const statusLabel: Record<string, string> = {
    PENDING: 'Esperando confirmación',
    PREPARING: 'Preparando tu pedido',
    READY: 'Listo — buscando repartidor',
    ASSIGNED: 'Repartidor en camino al negocio',
    PICKED_UP: 'En camino a tu dirección',
    DELIVERED: 'Entregado',
    CANCELLED: 'Cancelado',
  };
  const showRating = order.status === 'DELIVERED' && order.rated !== true;

  // Polyline real: si Directions API ya respondió, usa los puntos siguiendo
  // calles. Si aún no, fallback a línea recta (driver → destino del leg).
  // SOLO con posición fresca: una ruta trazada desde un punto viejo dibuja un
  // trayecto que el repartidor no está haciendo.
  let polyline: any[] | null = null;
  if (seguimiento === 'EN_VIVO' && driverCoord) {
    if (route?.coordinates && route.coordinates.length > 1) {
      polyline = route.coordinates.map((c) => ({ latitude: c.lat, longitude: c.lng }));
    } else if (order.status === 'ASSIGNED' && restCoord) {
      polyline = [driverCoord, restCoord];
    } else if (order.status === 'PICKED_UP' && dropCoord) {
      polyline = [driverCoord, dropCoord];
    }
  }

  return (
    <SafeAreaView style={styles.safe}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => volver(router)} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color={Colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>
          {terminal ? 'Tu pedido' : 'Seguir mi pedido'}
        </Text>
        <View style={{ width: 24 }} />
      </View>

      <View style={styles.mapWrap}>
        <MapView
          style={{ flex: 1 }}
          region={{
            latitude: region.latitude,
            longitude: region.longitude,
            latitudeDelta: 0.025,
            longitudeDelta: 0.025,
          }}
        >
          {restCoord && (
            <MapMarker
              coordinate={restCoord}
              title={order.restaurantName ?? 'Negocio'}
              description="Negocio"
              iconEmoji="🏪"
            />
          )}
          {dropCoord && (
            <MapMarker
              coordinate={dropCoord}
              title="Tu dirección"
              description={destinoDireccion ?? undefined}
              iconEmoji="🏠"
            />
          )}
          {driverCoord && (seguimiento === 'EN_VIVO' || seguimiento === 'RANCIA') && (
            <MapMarker
              coordinate={driverCoord}
              title="Repartidor"
              description={
                seguimiento === 'EN_VIVO'
                  ? 'Posición en vivo'
                  : 'Última posición recibida'
              }
              iconEmoji="🛵"
            />
          )}
          {polyline && polyline.length >= 2 && (
            <MapPolyline coordinates={polyline} strokeColor={Colors.primary} strokeWidth={4} />
          )}
        </MapView>
      </View>

      <View style={styles.bottomCard}>
        <View style={styles.statusRow}>
          <View>
            <Text style={styles.statusLabel}>{statusLabel[order.status] ?? order.status}</Text>
            <Text style={styles.restName}>{order.restaurantName}</Text>
          </View>
          {!terminal && (
            (() => {
              // Prioridad: Directions API real > haversine aproximado.
              // El tiempo de la ruta solo vale si la posición que lo originó
              // sigue viva; si no, se cae al ETA del servidor o a nada.
              const realMin =
                seguimiento === 'EN_VIVO' && route?.durationSeconds
                  ? Math.max(1, Math.round(route.durationSeconds / 60))
                  : null;
              const display = realMin ?? eta?.etaMinutes;
              if (!display || display <= 0) return null;
              return (
                <View style={styles.etaBadge}>
                  <Ionicons name="time" size={16} color="#FFF" />
                  <Text style={styles.etaText}>
                    {display} min
                    {realMin && route?.distanceText ? ` · ${route.distanceText}` : ''}
                  </Text>
                </View>
              );
            })()
          )}
        </View>

        <View style={styles.divider} />

        {/* ── Estado del seguimiento ──────────────────────────────────────
            El mapa de arriba no habla; este bloque sí. Nunca se queda mudo
            prometiendo un seguimiento en vivo que no existe. */}
        {seguimiento === 'TERMINADO' && (
          <View style={styles.segNeutro}>
            <Ionicons
              name={order.status === 'DELIVERED' ? 'checkmark-circle' : 'close-circle'}
              size={18}
              color={order.status === 'DELIVERED' ? '#2E7D32' : '#B00020'}
            />
            <View style={{ flex: 1, marginLeft: 10 }}>
              <Text style={styles.segTitulo}>
                {order.status === 'DELIVERED' ? 'Pedido entregado' : 'Pedido cancelado'}
              </Text>
              <Text style={styles.segDesc}>
                {/* H25, auditoría 16-sep-2026 — el cierre automático de un
                    pedido tomado que nadie recogió (foodDispatch.ts) se explica
                    aquí; sin esto el cliente solo leía «cancelado». */}
                {order.status === 'CANCELLED' && order.cierreAutomatico === 'SIN_RECOGER'
                  ? 'Lo cerramos porque el repartidor que lo tomó no lo recogió a tiempo. No cuenta como cancelación tuya.'
                  : order.status === 'CANCELLED' && order.cierreAutomatico === 'SIN_REPARTIDOR'
                  ? 'Lo cerramos porque ningún repartidor lo tomó a tiempo. No cuenta como cancelación tuya.'
                  : 'Ya no hay nada que seguir: el mapa dejó de actualizarse.'}
              </Text>
            </View>
          </View>
        )}

        {seguimiento === 'SIN_REPARTIDOR' && (
          <View style={styles.segNeutro}>
            <Ionicons name="person-outline" size={18} color={Colors.textSecondary} />
            <View style={{ flex: 1, marginLeft: 10 }}>
              <Text style={styles.segTitulo}>Todavía no hay repartidor asignado</Text>
              <Text style={styles.segDesc}>
                Cuando alguien tome tu pedido, aparecerá en el mapa. Por ahora solo se ven
                el negocio y tu dirección.
              </Text>
            </View>
          </View>
        )}

        {seguimiento === 'BUSCANDO' && (
          <View style={styles.segNeutro}>
            <ActivityIndicator size="small" color={Colors.primary} />
            <View style={{ flex: 1, marginLeft: 10 }}>
              <Text style={styles.segTitulo}>Buscando la ubicación del repartidor…</Text>
              <Text style={styles.segDesc}>
                Si no aparece en unos segundos, te lo decimos aquí mismo.
              </Text>
            </View>
          </View>
        )}

        {seguimiento === 'RANCIA' && (
          <View style={styles.segAviso}>
            <Ionicons name="pause-circle-outline" size={18} color="#8B6E00" />
            <View style={{ flex: 1, marginLeft: 10 }}>
              <Text style={styles.segTituloAviso}>El seguimiento se detuvo</Text>
              <Text style={styles.segDescAviso}>
                {`Llevamos ${minutosSinActualizar} min sin recibir actualizaciones. El punto del mapa es la última posición que nos llegó, no la de ahora.`}
              </Text>
              <TouchableOpacity
                style={styles.segBoton}
                onPress={reintentarSeguimiento}
              >
                <Ionicons name="refresh" size={15} color={Colors.primary} />
                <Text style={styles.segBotonTexto}>Reintentar</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {seguimiento === 'SIN_SENAL' && (
          <View style={styles.segAviso}>
            <Ionicons name="location-outline" size={18} color="#8B6E00" />
            <View style={{ flex: 1, marginLeft: 10 }}>
              <Text style={styles.segTituloAviso}>No podemos mostrar al repartidor en el mapa</Text>
              <Text style={styles.segDescAviso}>
                {`No nos llega su ubicación. Tu pedido sigue su curso: ${statusLabel[order.status] ?? order.status}.` +
                  (order.driverId ? ' Puedes escribirle por el chat aquí abajo.' : '')}
              </Text>
              <View style={styles.segBotonera}>
                <TouchableOpacity
                  style={styles.segBoton}
                  onPress={reintentarSeguimiento}
                >
                  <Ionicons name="refresh" size={15} color={Colors.primary} />
                  <Text style={styles.segBotonTexto}>Reintentar</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.segBoton}
                  onPress={() => router.push(`/order-receipt?orderId=${order.id}` as any)}
                >
                  <Ionicons name="receipt-outline" size={15} color={Colors.primary} />
                  <Text style={styles.segBotonTexto}>Ver detalle</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}

        {/* Banner: TRANSFER en validación */}
        {order.paymentMethod === 'TRANSFER' && order.status === 'PENDING' && (
          <View style={styles.transferValidating}>
            <Ionicons name="hourglass-outline" size={18} color="#F57F17" />
            <View style={{ flex: 1, marginLeft: 10 }}>
              <Text style={styles.transferValidatingTitle}>Comprobante en validación</Text>
              <Text style={styles.transferValidatingDesc}>
                El negocio está revisando tu transferencia. Tu pedido entrará en preparación apenas confirme el pago.
              </Text>
            </View>
          </View>
        )}
        {/* Banner: CARD pago confirmado */}
        {order.paymentMethod === 'CARD' && order.paymentStatus === 'PAID' && (
          <View style={styles.paidBanner}>
            <Ionicons name="shield-checkmark" size={18} color="#2E7D32" />
            <Text style={styles.paidBannerText}>Pago confirmado por Stripe · 0% comisión Go Safely</Text>
          </View>
        )}
        {/* Banner: CARD pago falló */}
        {order.paymentMethod === 'CARD' && order.paymentStatus === 'FAILED' && (
          <View style={styles.failedBanner}>
            <Ionicons name="alert-circle" size={18} color="#FFF" />
            <Text style={styles.failedBannerText}>
              Pago rechazado{order.paymentFailureReason ? ` · ${order.paymentFailureReason}` : ''}. Intenta con otra tarjeta o método.
            </Text>
          </View>
        )}
        {/* Banner: REFUNDED */}
        {order.paymentStatus === 'REFUNDED' && (
          <View style={styles.refundBanner}>
            <Ionicons name="cash" size={18} color="#FFF" />
            <Text style={styles.refundBannerText}>
              Reembolso procesado{typeof order.refundAmountCents === 'number' ? ` · ${pesos(order.refundAmountCents / 100)}` : ''} · llega en 5-10 días hábiles a tu tarjeta
            </Text>
          </View>
        )}

        {/* El PIN solo tiene sentido mientras queda una entrega por hacer.
            Y '----' con pinta de dato era otra promesa muda: si no llegó,
            se dice que no llegó. */}
        {!terminal && (
          <View style={styles.pinBox}>
            <Ionicons name="key" size={22} color="#FF9500" />
            <View style={{ marginLeft: 10, flex: 1 }}>
              <Text style={styles.pinLabel}>Tu PIN al repartidor</Text>
              {orderPin ? (
                <Text style={styles.pinValue}>{orderPin}</Text>
              ) : ahora - pinDesde < GRACIA_PIN_MS ? (
                <Text style={styles.pinPendiente}>Cargando…</Text>
              ) : (
                <>
                  <Text style={styles.pinPendiente}>No disponible</Text>
                  <Text style={styles.pinNota}>
                    {order.driverId
                      ? 'No pudimos leer tu PIN. Sal y vuelve a entrar a esta pantalla; si sigue sin aparecer, acuérdalo con el repartidor por el chat antes de recibir el pedido.'
                      : 'No pudimos leer tu PIN. Sal y vuelve a entrar a esta pantalla; aparecerá aquí antes de que el repartidor llegue.'}
                  </Text>
                </>
              )}
            </View>
          </View>
        )}

        {order.driverId && (
          <TouchableOpacity
            style={styles.chatBtn}
            onPress={() => router.push(`/order-chat?orderId=${order.id}` as any)}
          >
            <Ionicons name="chatbubble-outline" size={18} color={Colors.primary} />
            <Text style={styles.chatBtnText}>Chatear con el repartidor</Text>
          </TouchableOpacity>
        )}

        {showRating && (
          <TouchableOpacity
            style={styles.rateBtn}
            onPress={() => router.push(`/rate-order?orderId=${order.id}` as any)}
          >
            <Ionicons name="star" size={18} color="#FFF" />
            <Text style={styles.rateBtnText}>Calificar pedido</Text>
          </TouchableOpacity>
        )}
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#FFF' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  backBtn: { padding: 5 },
  headerTitle: { fontSize: 17, fontWeight: '700', color: Colors.text },
  mapWrap: { flex: 1 },
  bottomCard: {
    backgroundColor: '#FFF',
    padding: SIZES.padding,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowOffset: { width: 0, height: -2 },
    elevation: 8,
  },
  statusRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  statusLabel: { fontSize: 16, fontWeight: '700', color: Colors.text },
  restName: { fontSize: 13, color: Colors.textSecondary, marginTop: 4 },
  etaBadge: {
    backgroundColor: Colors.primary,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  etaText: { color: '#FFF', fontWeight: '700', marginLeft: 4 },
  divider: { height: 1, backgroundColor: '#F0F0F0', marginVertical: 12 },
  pinBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF8E1',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#FFE082',
  },
  pinLabel: { fontSize: 12, color: Colors.textSecondary },
  pinValue: { fontSize: 22, fontWeight: '900', color: Colors.text, letterSpacing: 2 },
  pinPendiente: { fontSize: 15, fontWeight: '700', color: Colors.textSecondary, marginTop: 2 },
  pinNota: { fontSize: 11, color: Colors.textSecondary, marginTop: 4, lineHeight: 15 },

  // Escudo de carga y pantallas vacías (con salida)
  cargandoTexto: { marginTop: 12, fontSize: 13, color: Colors.textSecondary },
  vacioTitulo: { marginTop: 12, fontSize: 16, fontWeight: '800', color: Colors.text, textAlign: 'center' },
  vacioDesc: {
    marginTop: 6,
    fontSize: 13,
    color: Colors.textSecondary,
    textAlign: 'center',
    lineHeight: 18,
  },
  botonSalida: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EAF4FF',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
    marginTop: 18,
  },
  botonSalidaTexto: { color: Colors.primary, fontWeight: '700', marginLeft: 8 },

  // Estado del seguimiento
  segNeutro: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#F5F7FA',
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E3E8EF',
    marginBottom: 12,
  },
  segTitulo: { color: Colors.text, fontWeight: '800', fontSize: 13 },
  segDesc: { color: Colors.textSecondary, fontSize: 11, marginTop: 2, lineHeight: 15 },
  segAviso: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFF8E1',
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#FFD54F',
    marginBottom: 12,
  },
  segTituloAviso: { color: '#6B5500', fontWeight: '800', fontSize: 13 },
  segDescAviso: { color: '#8B6E00', fontSize: 11, marginTop: 2, lineHeight: 15 },
  segBotonera: { flexDirection: 'row', flexWrap: 'wrap' },
  segBoton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EAF4FF',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginTop: 10,
    marginRight: 8,
  },
  segBotonTexto: { color: Colors.primary, fontWeight: '700', fontSize: 12, marginLeft: 6 },
  chatBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EAF4FF',
    paddingVertical: 12,
    borderRadius: 12,
    marginTop: 10,
  },
  chatBtnText: { color: Colors.primary, fontWeight: '700', marginLeft: 8 },
  rateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF9500',
    paddingVertical: 14,
    borderRadius: 12,
    marginTop: 10,
  },
  rateBtnText: { color: '#FFF', fontWeight: '700', marginLeft: 8 },

  // Banners de estado de pago
  transferValidating: {
    flexDirection: 'row',
    backgroundColor: '#FFF8E1',
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#FFD54F',
    marginBottom: 12,
  },
  transferValidatingTitle: { color: '#6B5500', fontWeight: '800', fontSize: 13 },
  transferValidatingDesc: { color: '#8B6E00', fontSize: 11, marginTop: 2, lineHeight: 15 },

  paidBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E8F5E9',
    padding: 10,
    borderRadius: 10,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#A5D6A7',
  },
  paidBannerText: { color: '#1B5E20', fontWeight: '700', fontSize: 12, marginLeft: 8, flex: 1 },

  failedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#D32F2F',
    padding: 12,
    borderRadius: 10,
    marginBottom: 12,
  },
  failedBannerText: { color: '#FFF', fontWeight: '700', fontSize: 12, marginLeft: 8, flex: 1 },

  refundBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1565C0',
    padding: 12,
    borderRadius: 10,
    marginBottom: 12,
  },
  refundBannerText: { color: '#FFF', fontWeight: '700', fontSize: 12, marginLeft: 8, flex: 1 },
});
